import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { router } from '../lib/router.js';

/**
 * Login Page Component
 * Handles user authentication
 */
export class LoginPage extends BaseComponent {
  private isLoading = false;
  private errorMessage = '';

  protected render(): void {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'login-container' });

    // Logo and title
    const header = this.createElement('div', { className: 'login-header' });
    const title = this.createElement('h1', {}, 'FireGuard');
    const subtitle = this.createElement('p', { className: 'subtitle' }, 'Fire Extinguisher Compliance Tracking');
    header.appendChild(title);
    header.appendChild(subtitle);
    container.appendChild(header);

    // Login form card
    const card = this.createElement('div', { className: 'login-card' });
    const cardTitle = this.createElement('h2', {}, 'Sign In');
    card.appendChild(cardTitle);

    // Error message
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      card.appendChild(error);
    }

    // Form
    const form = this.createElement('form', { className: 'login-form' });
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Email field
    const emailGroup = this.createElement('div', { className: 'form-group' });
    const emailLabel = this.createElement('label', { for: 'email' }, 'Email');
    const emailInput = this.createElement('input', {
      type: 'email',
      id: 'email',
      name: 'email',
      required: 'true',
      placeholder: 'admin@company.com',
    });
    emailGroup.appendChild(emailLabel);
    emailGroup.appendChild(emailInput);
    form.appendChild(emailGroup);

    // Password field
    const passwordGroup = this.createElement('div', { className: 'form-group' });
    const passwordLabel = this.createElement('label', { for: 'password' }, 'Password');
    const passwordInput = this.createElement('input', {
      type: 'password',
      id: 'password',
      name: 'password',
      required: 'true',
      placeholder: '••••••••',
    });
    passwordGroup.appendChild(passwordLabel);
    passwordGroup.appendChild(passwordInput);
    form.appendChild(passwordGroup);

    // Submit button
    const submitBtn = this.createElement('button', {
      type: 'submit',
      className: 'btn-primary',
    }, this.isLoading ? 'Signing in...' : 'Sign In');
    if (this.isLoading) {
      submitBtn.setAttribute('disabled', 'true');
    }
    form.appendChild(submitBtn);

    card.appendChild(form);

    // Register link
    const footer = this.createElement('div', { className: 'login-footer' });
    const forgotText = this.createElement('p', {});
    forgotText.appendChild(this.createElement('a', { href: '/forgot-password' }, 'Forgot your password?'));
    footer.appendChild(forgotText);
    const footerText = this.createElement('p', {}, "Don't have an account? ");
    const registerLink = this.createElement('a', { href: '/register' }, 'Sign Up');
    footerText.appendChild(registerLink);
    footer.appendChild(footerText);

    // Legal links
    const legalText = this.createElement('p', { className: 'legal-links' });
    const privacyLink = this.createElement('a', { href: '/privacy-policy' }, 'Privacy Policy');
    const separator = document.createTextNode(' • ');
    const termsLink = this.createElement('a', { href: '/terms-of-service' }, 'Terms of Service');
    legalText.appendChild(privacyLink);
    legalText.appendChild(separator);
    legalText.appendChild(termsLink);
    footer.appendChild(legalText);

    card.appendChild(footer);

    container.appendChild(card);
    this.shadow.appendChild(container);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    this.isLoading = true;
    this.errorMessage = '';
    this.shadow.innerHTML = '';
    this.render();

    try {
      await api.login({ email, password });
      // Navigate to dashboard on success
      router.navigate('/dashboard');
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Login failed';
      this.shadow.innerHTML = '';
      this.render();
    }
  }

  private getStyles(): string {
    return `
      .login-container {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%);
        padding: var(--spacing-lg);
      }

      .login-header {
        text-align: center;
        margin-bottom: var(--spacing-lg);
      }

      .login-header h1 {
        font-size: 48px;
        font-weight: 700;
        color: var(--danger);
        margin-bottom: var(--spacing-sm);
      }

      .subtitle {
        font-size: 16px;
        color: var(--gray-500);
      }

      .login-card {
        background: white;
        border-radius: var(--border-radius);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        padding: 32px;
        width: 100%;
        max-width: 400px;
      }

      .login-card h2 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-lg);
        text-align: center;
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
        font-size: 14px;
      }

      .login-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      label {
        font-size: 14px;
        font-weight: 500;
        color: var(--gray-700);
      }

      input {
        padding: 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 16px;
        transition: border-color 0.2s;
      }

      input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .btn-primary {
        padding: 12px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--primary-dark);
      }

      .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .login-footer {
        margin-top: var(--spacing-lg);
        text-align: center;
        font-size: 14px;
        color: var(--gray-500);
      }

      .login-footer a {
        color: var(--primary);
        text-decoration: none;
        font-weight: 500;
      }

      .login-footer a:hover {
        text-decoration: underline;
      }

      .legal-links {
        margin-top: var(--spacing-md);
        font-size: 12px;
        color: var(--gray-400);
      }

      .legal-links a {
        color: var(--gray-500);
        font-weight: 400;
      }
    `;
  }
}

// Register custom element
customElements.define('login-page', LoginPage);
